import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    question: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitStatus({ type: 'success', message: response.data.message });
        setFormData({
          nom: '',
          prenom: '',
          telephone: '',
          email: '',
          question: '',
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Une erreur est survenue. Veuillez réessayer ou nous contacter par téléphone.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Téléphone',
      subtitle: 'Direction Commerciale',
      value: '01.34.90.86.90',
      link: 'tel:0134908690',
    },
    {
      icon: Mail,
      title: 'Email',
      subtitle: 'Nous écrire',
      value: 'contact@pro-home-developpement.com',
      link: 'mailto:contact@pro-home-developpement.com',
    },
    {
      icon: MapPin,
      title: 'Adresse',
      subtitle: 'Siège social',
      value: '66 av. des Champs Élysées, 75008 Paris',
      link: null,
    },
    {
      icon: Clock,
      title: 'Horaires',
      subtitle: 'Du lundi au vendredi',
      value: '9h00 - 18h00',
      link: null,
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Contact</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0A0A0A] uppercase tracking-tighter leading-tight mb-6">
              Parlez-nous de<br />
              <span className="text-[#F25F19]">votre projet</span>
            </h1>
            <p className="text-lg md:text-xl text-[#71717A] max-w-xl">
              Notre équipe est à votre écoute pour étudier votre projet et vous 
              proposer les solutions adaptées.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b border-[#E4E4E7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="p-6 bg-[#F4F4F5] rounded-sm hover:bg-[#0A0A0A] group transition-colors duration-300"
              >
                <info.icon className="w-8 h-8 text-[#F25F19] mb-4" />
                <h3 className="font-bold text-lg mb-1 group-hover:text-white transition-colors">
                  {info.title}
                </h3>
                <p className="text-[#71717A] text-sm mb-2 group-hover:text-white/60 transition-colors">
                  {info.subtitle}
                </p>
                {info.link ? (
                  <a
                    href={info.link}
                    className="text-[#0A0A0A] font-medium group-hover:text-[#F25F19] transition-colors mono"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-[#0A0A0A] font-medium group-hover:text-white transition-colors">
                    {info.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section data-testid="contact-form-section" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mb-4">
                Demande de devis
              </h2>
              <p className="text-[#71717A] mb-8">
                Remplissez le formulaire ci-dessous et notre équipe vous recontactera 
                dans les plus brefs délais.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium mb-2">
                      Nom *
                    </label>
                    <Input
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      placeholder="Votre nom"
                      className="form-input"
                      data-testid="input-nom"
                    />
                  </div>
                  <div>
                    <label htmlFor="prenom" className="block text-sm font-medium mb-2">
                      Prénom *
                    </label>
                    <Input
                      id="prenom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      placeholder="Votre prénom"
                      className="form-input"
                      data-testid="input-prenom"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="telephone" className="block text-sm font-medium mb-2">
                      Téléphone *
                    </label>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      placeholder="01 23 45 67 89"
                      className="form-input"
                      data-testid="input-telephone"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Adresse mail *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.com"
                      className="form-input"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="question" className="block text-sm font-medium mb-2">
                    Votre question *
                  </label>
                  <Textarea
                    id="question"
                    name="question"
                    value={formData.question}
                    onChange={handleChange}
                    required
                    placeholder="Posez votre question..."
                    rows={6}
                    className="form-input resize-none"
                    data-testid="input-question"
                  />
                </div>

                {submitStatus && (
                  <div
                    className={`p-4 rounded-sm flex items-start gap-3 ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 text-green-800'
                        : 'bg-red-50 text-red-800'
                    }`}
                    data-testid="submit-status"
                  >
                    {submitStatus.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    )}
                    <p>{submitStatus.message}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#F25F19] hover:bg-[#CC480B] text-white font-bold uppercase tracking-wide py-6 rounded-sm disabled:opacity-50"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      Envoyer ma demande
                      <Send className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>

                <p className="text-[#71717A] text-sm text-center">
                  * Champs obligatoires. Vos données sont traitées conformément à notre 
                  politique de confidentialité.
                </p>
              </form>
            </div>

            {/* Side Info */}
            <div className="lg:pl-8">
              <div className="bg-[#0A0A0A] p-8 lg:p-12 rounded-sm sticky top-32">
                <h3 className="text-2xl font-extrabold text-white tracking-tight mb-6">
                  Pourquoi nous choisir ?
                </h3>
                <ul className="space-y-4 mb-8">
                  {[
                    'Plus de 20 ans d\'expérience',
                    'Partenaire des bailleurs sociaux',
                    'Garanties constructeurs',
                    'Accompagnement personnalisé',
                    'Devis gratuit et sans engagement',
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#F25F19] flex-shrink-0" />
                      <span className="text-white/80">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t border-[#18181B] pt-8">
                  <p className="text-[#71717A] text-sm mb-4">
                    Besoin d'une réponse rapide ?
                  </p>
                  <a
                    href="tel:0134908690"
                    className="flex items-center gap-3 text-white hover:text-[#F25F19] transition-colors"
                  >
                    <div className="w-12 h-12 bg-[#F25F19] rounded-sm flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold mono">01.34.90.86.90</p>
                      <p className="text-white/60 text-sm">Lun-Ven 9h-18h</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 bg-[#F4F4F5] relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-16 h-16 text-[#F25F19] mx-auto mb-4" />
            <p className="text-[#71717A] text-lg">PRO HOME Développement</p>
            <p className="text-[#0A0A0A] font-bold">66 avenue des Champs Élysées</p>
            <p className="text-[#0A0A0A] font-bold">75008 Paris</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
